import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '../../../api/apiClient';
import { GET_ARTISTS_URL } from '../../../api/artist';
import { GET_SONGS_URL } from '../../../api/song';

function FormSelect({
  label,
  required,
  options = [],
  placeholder = 'Select option',
  value, // string/number or array of string/number
  onChange,
  isMulti = false,
  onAddNewLabel, // if provided, shows "+ Add New" next to the label
  onAddNewInput, // if provided, shows "+ Add New" next to the input
  disabled = false,
  isLazy = false,
  roleFilter = '',
  lazyType = 'artists',
  lazyParams = {},
  excludeIds = [],
  // Main artist selection props (optional — only for multi-select artist roles)
  mainValue = null,     // ID of the currently selected main artist
  onMainChange = null,  // callback(id) when a new main is selected
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);

  // Lazy loading state
  const [lazyOptions, setLazyOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const capitalizeName = (name) => {
    if (!name) return '';
    return name
      .split(' ')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  };

  // Fetch paginated options from BE
  const fetchLazyOptions = async (pageNumber, searchVal, isAppend) => {
    if (isLoading) return;
    try {
      setIsLoading(true);
      const queryParamsObj = {
        page: pageNumber,
        limit: 20,
        search: searchVal,
        ...lazyParams,
      };
      // For artist dropdowns, always load ALL active artists (status=1, is_delete=0)
      // regardless of their singer/lyrics/music role flags — do NOT filter by role
      if (lazyType !== 'songs') {
        queryParamsObj.status = 1;
      }
      const queryParams = new URLSearchParams(queryParamsObj).toString();
      const fetchUrl = lazyType === 'songs' ? GET_SONGS_URL : GET_ARTISTS_URL;

      const response = await apiClient(`${fetchUrl}?${queryParams}`);
      if (lazyType === 'songs') {
        if (response && response.songs) {
          const mapped = response.songs.map((song) => ({
            value: String(song.id),
            label: song.nameSinhala ? `${song.nameSinhala} (${song.name})` : song.name,
            rawName: song.name,
          }));

          if (isAppend) {
            setLazyOptions((prev) => {
              const existingIds = new Set(prev.map((o) => o.value));
              const filtered = mapped.filter((o) => !existingIds.has(o.value));
              return [...prev, ...filtered];
            });
          } else {
            setLazyOptions(mapped);
          }

          const loadedCount = isAppend ? lazyOptions.length + mapped.length : mapped.length;
          setHasMore(mapped.length === 20 && loadedCount < response.totalCount);
        }
      } else {
        if (response && response.artists) {
          const mapped = response.artists.map((artist) => ({
            value: String(artist.id),
            label: `${artist.code || artist.artist_code} - ${capitalizeName(artist.name)}`,
            rawName: capitalizeName(artist.name),
          }));

          if (isAppend) {
            setLazyOptions((prev) => {
              const existingIds = new Set(prev.map((o) => o.value));
              const filtered = mapped.filter((o) => !existingIds.has(o.value));
              return [...prev, ...filtered];
            });
          } else {
            setLazyOptions(mapped);
          }

          const loadedCount = isAppend ? lazyOptions.length + mapped.length : mapped.length;
          setHasMore(mapped.length === 20 && loadedCount < response.totalCount);
        }
      }
    } catch (err) {
      console.warn('Failed to load lazy select options:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Trigger lazy fetching when dropdown opens, or when page/search changes
  useEffect(() => {
    if (!isLazy || !isOpen) return;
    setPage(1);
    setHasMore(true);
    fetchLazyOptions(1, searchQuery, false);
  }, [isOpen, isLazy]);

  useEffect(() => {
    if (!isLazy || !isOpen) return;
    const delayDebounce = setTimeout(() => {
      setPage(1);
      setHasMore(true);
      fetchLazyOptions(1, searchQuery, false);
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  useEffect(() => {
    if (!isLazy || !isOpen || page === 1) return;
    fetchLazyOptions(page, searchQuery, true);
  }, [page]);

  // Pre-fetch details for selected IDs not yet in lazyOptions
  useEffect(() => {
    if (!isLazy || !value) return;
    const selectedIds = (Array.isArray(value) ? value : [value])
      .map(String)
      .filter(Boolean);

    const missingIds = selectedIds.filter(
      (id) => !lazyOptions.some((opt) => opt.value === id)
    );
    if (missingIds.length === 0) return;

    const fetchMissing = async () => {
      try {
        const fetched = await Promise.all(
          missingIds.map(async (id) => {
            try {
              const fetchUrl = lazyType === 'songs' ? GET_SONGS_URL : GET_ARTISTS_URL;
              const item = await apiClient(`${fetchUrl}/${id}`);
              if (item) {
                if (lazyType === 'songs') {
                  return {
                    value: String(item.id),
                    label: item.nameSinhala ? `${item.nameSinhala} (${item.name})` : item.name,
                    rawName: item.name,
                  };
                } else {
                  return {
                    value: String(item.id),
                    label: `${item.artist_code || item.code} - ${capitalizeName(item.name)}`,
                    rawName: capitalizeName(item.name),
                  };
                }
              }
            } catch (e) {
              console.warn(`Failed to fetch lazy details for ID ${id}:`, e);
            }
            return null;
          })
        );
        const validFetched = fetched.filter(Boolean);
        if (validFetched.length > 0) {
          setLazyOptions((prev) => {
            const existingIds = new Set(prev.map((o) => o.value));
            const filtered = validFetched.filter((o) => !existingIds.has(o.value));
            return [...prev, ...filtered];
          });
        }
      } catch (err) {
        console.warn('Failed to pre-fetch missing selected options:', err);
      }
    };
    fetchMissing();
  }, [value, isLazy, lazyOptions.length]);

  const displayOptions = isLazy ? lazyOptions : options;

  const normalizedExcludeIds = (Array.isArray(excludeIds) ? excludeIds : []).map(String);

  // Filter options based on excludeIds and search query
  const filteredOptions = displayOptions.filter((opt) => {
    if (normalizedExcludeIds.includes(String(opt.value))) {
      return false;
    }
    if (!isLazy && searchQuery) {
      return opt.label.toLowerCase().includes(searchQuery.toLowerCase());
    }
    return true;
  });

  // Helper to check if an option is selected
  const isSelected = (optVal) => {
    if (isMulti) {
      return Array.isArray(value) && value.map(String).includes(String(optVal));
    }
    return String(value) === String(optVal);
  };

  // Handle option click
  const handleSelect = (optVal) => {
    if (isMulti) {
      const currentValues = Array.isArray(value) ? value.map(String) : [];
      if (currentValues.includes(String(optVal))) {
        // Removing — if this was the main, reassign main to first remaining
        const remaining = currentValues.filter((val) => String(val) !== String(optVal));
        onChange(remaining);
        if (onMainChange && String(mainValue) === String(optVal)) {
          onMainChange(remaining.length > 0 ? remaining[0] : null);
        }
      } else {
        const newValues = [...currentValues, String(optVal)];
        onChange(newValues);
        // Auto-set as main if no main is assigned yet
        if (onMainChange && !mainValue) {
          onMainChange(String(optVal));
        }
      }
    } else {
      onChange(String(optVal));
      setIsOpen(false);
    }
    setSearchQuery('');
  };

  // Text display inside input field
  const getDisplayText = () => {
    if (isMulti) {
      const currentValues = Array.isArray(value) ? value.map(String) : [];
      if (currentValues.length === 0) return '';
      
      return displayOptions
        .filter((opt) => currentValues.includes(String(opt.value)))
        .map((opt) => opt.rawName || opt.label)
        .join(', ');
    } else {
      const selectedOpt = displayOptions.find((opt) => String(opt.value) === String(value));
      return selectedOpt ? (selectedOpt.rawName || selectedOpt.label) : '';
    }
  };

  // Remove a multi-selected item tag
  const handleRemoveItem = (e, optVal) => {
    e.stopPropagation();
    if (disabled) return;
    if (isMulti && Array.isArray(value)) {
      const remaining = value.map(String).filter((val) => String(val) !== String(optVal));
      onChange(remaining);
      // If removed item was the main, reassign main to first remaining
      if (onMainChange && String(mainValue) === String(optVal)) {
        onMainChange(remaining.length > 0 ? remaining[0] : null);
      }
    }
  };

  // Handle clicking the crown/star to set a pill as main
  const handleSetMain = (e, optVal) => {
    e.stopPropagation();
    if (disabled || !onMainChange) return;
    onMainChange(String(optVal));
  };

  const selectedList = isMulti && Array.isArray(value)
    ? displayOptions.filter((opt) => value.map(String).includes(String(opt.value)))
    : [];

  // Whether main-selection mode is active (multi with main props provided)
  const isMainMode = isMulti && typeof onMainChange === 'function';

  const handleScroll = (e) => {
    if (!isLazy || !hasMore || isLoading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 15) {
      setPage((prev) => prev + 1);
    }
  };

  return (
    <div className="form-select-container" ref={containerRef}>
      {/* Label Row */}
      {(label || onAddNewLabel) && (
        <div className="form-label-row">
          <label className="form-field-label">
            {label} {required && <span className="required-asterisk">*</span>}
          </label>
          {onAddNewLabel && !disabled && (
            <button 
              type="button" 
              className="add-new-link-btn" 
              onClick={onAddNewLabel}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <span>Add New</span>
            </button>
          )}
        </div>
      )}

      {/* Input Row */}
      <div className="form-select-input-wrapper">
        <div 
          className={`form-select-trigger-box ${isOpen ? 'open' : ''} ${value && (isMulti ? value.length > 0 : true) ? 'has-value' : ''} ${disabled ? 'disabled' : ''}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          style={disabled ? { backgroundColor: '#f1f5f9', cursor: 'not-allowed', opacity: 0.8 } : undefined}
        >
          {isMulti && selectedList.length > 0 ? (
            <div className="select-pill-badges-container">
              {selectedList.map((item) => {
                const isThisMain = isMainMode && String(mainValue) === String(item.value);
                const showCrown = isMainMode && selectedList.length > 1;
                return (
                  <span
                    key={item.value}
                    className={`select-pill-badge${isThisMain ? ' is-main' : ''}`}
                    title={isThisMain ? 'Main artist (receives revenue for this role)' : isMainMode ? 'Click ★ to set as main' : ''}
                  >
                    {/* Crown/star icon — only shown when multiple artists are selected */}
                    {showCrown && (
                      <button
                        type="button"
                        className={`pill-main-btn${isThisMain ? ' active' : ''}`}
                        onClick={(e) => handleSetMain(e, item.value)}
                        title={isThisMain ? 'Main artist for revenue' : 'Set as main artist'}
                      >
                        {/* Crown SVG */}
                        <svg width="11" height="11" viewBox="0 0 24 24" fill={isThisMain ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                      </button>
                    )}
                    {item.rawName || item.label}
                    <button 
                      type="button" 
                      className="select-pill-badge-remove"
                      onClick={(e) => handleRemoveItem(e, item.value)}
                    >
                      &times;
                    </button>
                  </span>
                );
              })}
            </div>
          ) : (
            <span className={`select-placeholder ${getDisplayText() ? 'selected-value' : ''}`}>
              {getDisplayText() || placeholder}
            </span>
          )}

          <div className="select-chevrons">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`chevron-icon ${isOpen ? 'rotate' : ''}`}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
        </div>

        {onAddNewInput && !disabled && (
          <button 
            type="button" 
            className="add-new-action-btn-outlined" 
            onClick={onAddNewInput}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>Add New</span>
          </button>
        )}

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="select-dropdown-menu">
            <div className="select-dropdown-search-box">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" className="search-icon">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input 
                type="text" 
                className="select-dropdown-search-input"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>

            <div className="select-options-list" onScroll={handleScroll}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((opt) => (
                  <div
                    key={opt.value}
                    className={`select-option-item ${isSelected(opt.value) ? 'active' : ''}`}
                    onClick={() => handleSelect(opt.value)}
                  >
                    <span>{opt.label}</span>
                    {isSelected(opt.value) && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="checkmark-icon">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                ))
              ) : (
                <div className="select-no-options">
                  {isLoading ? 'Loading...' : 'No options found'}
                </div>
              )}
              {isLoading && filteredOptions.length > 0 && (
                <div className="select-no-options" style={{ padding: '8px', fontSize: '12px' }}>
                  Loading more...
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default FormSelect;
